import {
  useCallback, useEffect,
} from 'react';
import { useDispatch } from 'react-redux';
import { useStorageService } from '~/_shared/utils/storageService';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import { useModal } from '~/modal/useModal.hook';
import { useCanUserInitiateMigrationSelector } from '~/store/mapMigration/mapMigration.selectors';
import { userSettingsSetShownWelcomeModal } from '~/store/userData/settings/userDataSettings.actionCreators';
import { useUserSettingsShownWelcomeModal } from '~/store/userData/settings/userDataSettings.selectors';
import { useUserDataSelector } from '~/store/userData/userData.selectors';

const getOldSeenQuickstartKey = (userId: number) => `seenQuickstart|${userId}`;
const seenQuickstartKey = 'seenQuickstart';
const seenMigrationModalKey = 'seenMigrationModal';

export const useShowWelcomeModalOnFirstLogin = () => {
  const dispatch = useDispatch();
  const storageService = useStorageService();
  const { id: userId, clientId, previousLogin } = useUserDataSelector();
  const { openModal: openWelcomeModal } = useModal('Welcome');
  const { openModal: openMigrationModal } = useModal('MigrateMaps');
  const shownWelcomeModal = useUserSettingsShownWelcomeModal(clientId);
  const {
    isLoading: isMigrationStatusLoading,
    canUserInitiateMigration,
    userHasAlreadyMigratedSomeMaps,
  } = useCanUserInitiateMigrationSelector();

  const previousLoginKey = previousLogin?.toISOString() ?? 'first_login';
  const lastSeenMigrationModal = storageService.getLocalStorageItem(seenMigrationModalKey);
  const migrationModalAlreadySeen = lastSeenMigrationModal === previousLoginKey;

  const oldKey = notNullOrUndefined(userId) && getOldSeenQuickstartKey(userId);
  const oldSeenQuickstart = oldKey && localStorage.getItem(oldKey);
  const newSeenQuickstart = !!storageService.getLocalStorageItem(seenQuickstartKey);
  const seenQuickstart = notNullOrUndefined(oldSeenQuickstart) || newSeenQuickstart;

  const openWelcomeModalIfRequiredAndMarkSeen = useCallback(({ evenIfNotFirstLogin = false } = {}) => {
    if ((!previousLogin || evenIfNotFirstLogin) && !seenQuickstart && !shownWelcomeModal) {
      if (clientId) {
        dispatch(userSettingsSetShownWelcomeModal(clientId));
      }
      storageService.setLocalStorageItem(seenQuickstartKey, new Date().toISOString());
      openWelcomeModal();
    }
  }, [clientId, dispatch, openWelcomeModal, previousLogin, seenQuickstart, shownWelcomeModal, storageService]);

  // TODO: 2026/05/01: Remove the old key compatibility code.
  useEffect(() => {
    if (oldSeenQuickstart && !newSeenQuickstart) {
      // Transfers the old key to the new key
      storageService.setLocalStorageItem(seenQuickstartKey, new Date().toISOString());
    }
  }, [newSeenQuickstart, oldSeenQuickstart, storageService]);

  useEffect(() => {
    if (!userId || isMigrationStatusLoading) {
      return;
    }

    if (canUserInitiateMigration) {
      if (!userHasAlreadyMigratedSomeMaps) {
        if (migrationModalAlreadySeen) {
          storageService.setLocalStorageItem(seenMigrationModalKey, previousLoginKey);
          openMigrationModal({ onClose: openWelcomeModalIfRequiredAndMarkSeen });
        }
      }
      else {
        openWelcomeModalIfRequiredAndMarkSeen({ evenIfNotFirstLogin: true });
      }
      return; // we return here and don't continue, if we are showing migration it also handles showing welcome modal on close
    }

    openWelcomeModalIfRequiredAndMarkSeen();
  }, [previousLogin, previousLoginKey, openWelcomeModal, seenQuickstart, storageService, userId, isMigrationStatusLoading, canUserInitiateMigration, userHasAlreadyMigratedSomeMaps, openMigrationModal, openWelcomeModalIfRequiredAndMarkSeen, migrationModalAlreadySeen]);
};
